import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Users, Package, Sparkles, UserCog, ArrowUpRight } from "lucide-react";
import { api, ymd } from "@/lib/api";
import { PageHeader, Panel, Empty, ProductImage, StatusBadge } from "@/components/common";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const Stat = ({ icon: Icon, label, value, testId }) => (
  <Panel className="p-6 fade-up">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.18em] text-[#6E675F]">{label}</span>
      <Icon className="h-4 w-4 text-[#C5A059]" strokeWidth={1.5} />
    </div>
    <div className="mt-4 font-serif text-5xl text-[#121212]" data-testid={testId}>{value}</div>
  </Panel>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard", { params: { date: ymd(new Date()) } }).then((r) => setD(r.data)); }, []);
  const today = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div data-testid="dashboard-page">
      <PageHeader overline={today} title="Buongiorno" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Users} label="Clienti" value={d?.clients_count ?? "—"} testId="stat-clients" />
        <Stat icon={Sparkles} label="Trattamenti da fare" value={d?.planned_treatments ?? "—"} testId="stat-planned" />
        <Stat icon={UserCog} label="Operatori" value={d?.operators_count ?? "—"} testId="stat-operators" />
        <Stat icon={Package} label="Prodotti" value={d?.products_count ?? "—"} testId="stat-products" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <Panel className="p-6 lg:col-span-3 min-w-0">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-2xl">Appuntamenti di oggi</h2>
            <Link to="/calendario" className="text-sm text-[#C5A059] hover:underline flex items-center gap-1" data-testid="dashboard-calendar-link">Calendario <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </div>
          {d && d.today_appointments.length === 0 && <Empty testId="today-empty">Nessun appuntamento per oggi.</Empty>}
          <ul className="divide-y divide-[#E8E1D7]">
            {d?.today_appointments.map((a) => (
              <li key={a.id} className="flex items-center gap-3 sm:gap-4 py-3" data-testid={`today-appt-${a.id}`}>
                <div className="w-12 sm:w-14 shrink-0 font-serif text-xl text-[#121212]">{a.start.slice(11, 16)}</div>
                <span className="h-9 w-1 rounded-full" style={{ background: a.operator_color }} />
                <div className="flex-1 min-w-0">
                  <Link to={`/clienti/${a.client_id}`} className="block truncate font-medium text-[#121212] hover:text-[#C5A059]">{a.client_name}</Link>
                  <div className="truncate text-xs text-[#6E675F]">{a.treatment_name || "Trattamento"} · {a.operator_name || "nessun operatore"}</div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                  {a.status === "programmato" && <WhatsAppButton appt={a} compact />}
                  <StatusBadge status={a.status} />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel className="p-6 lg:col-span-2 min-w-0">
          <div className="flex items-center gap-2 mb-5">
            <AlertTriangle className="h-4 w-4 text-[#D97706]" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl">Scorte in esaurimento</h2>
          </div>
          {d && d.low_stock.length === 0 && <Empty testId="lowstock-empty">Tutte le scorte sono sopra la soglia.</Empty>}
          <ul className="space-y-3">
            {d?.low_stock.map((p) => (
              <li key={p.id} className="flex items-center gap-3" data-testid={`lowstock-${p.id}`}>
                <ProductImage path={p.photo_path} className="h-12 w-12 rounded-xl" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-[#6E675F]">Soglia {p.min_threshold}</div>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${p.quantity === 0 ? "bg-red-50 text-red-700" : "bg-amber-50 text-[#D97706]"}`}>{p.quantity} pz</span>
              </li>
            ))}
          </ul>
          {d?.low_stock.length > 0 && <Link to="/magazzino" className="mt-5 inline-block text-sm text-[#C5A059] hover:underline" data-testid="dashboard-inventory-link">Vai al magazzino</Link>}
        </Panel>
      </div>
    </div>
  );
}
